import React, { useEffect } from 'react';

const CircuitBreakers = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Circuit Breaker Pattern - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master circuit breaker pattern for fault tolerance. Learn to prevent cascading failures with automatic failure detection and recovery.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master circuit breaker pattern for fault tolerance. Learn to prevent cascading failures with automatic failure detection and recovery.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Circuit Breaker Pattern</h1>
        <p style={styles.subtitle}>
          Prevent cascading failures with automatic failure detection and recovery mechanisms
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is the Circuit Breaker Pattern?</h2>
          <p style={styles.paragraph}>
            The Circuit Breaker pattern is a design pattern that prevents cascading failures in distributed systems 
            by monitoring for failures and automatically "opening" the circuit when failure rates exceed a threshold. 
            Like an electrical circuit breaker, it stops the flow of requests to a failing service, allowing it time 
            to recover while protecting the calling system from waiting for timeouts.
          </p>
          <p style={styles.paragraph}>
            This pattern is essential for building resilient microservices architectures where service dependencies 
            can fail independently. It provides fast failure detection, automatic recovery testing, and fallback 
            mechanisms to maintain system stability.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Circuit Breaker States</h2>
          
          <h3 style={styles.heading}>Closed State</h3>
          <p style={styles.paragraph}>
            In the closed state, the circuit breaker allows all requests to pass through to the downstream service. 
            It monitors the success and failure rates, counting consecutive failures or calculating failure percentages.
          </p>
          <pre style={styles.codeBlock}>
{`Closed State Behavior:

Normal Operation:
┌─────────────┐    Request     ┌─────────────┐    Request     ┌─────────────┐
│   Client    │ ─────────────→ │   Circuit   │ ─────────────→ │   Service   │
│             │                │   Breaker   │                │             │
│             │ ←───────────── │  (CLOSED)   │ ←───────────── │             │
└─────────────┘    Response    └─────────────┘    Response    └─────────────┘

Characteristics:
✓ All requests pass through
✓ Monitor failure rate/count
✓ Track response times
✓ Collect success metrics

Monitoring Criteria:
- Consecutive failures (e.g., 5 in a row)
- Failure percentage (e.g., 50% in last 100 requests)
- Response time threshold (e.g., > 5 seconds)
- Exception types (timeout, connection error)

Transition to Open:
IF failure_count >= threshold OR
   failure_rate >= percentage OR
   avg_response_time >= timeout
THEN state = OPEN`}
          </pre>

          <h3 style={styles.heading}>Open State</h3>
          <p style={styles.paragraph}>
            In the open state, the circuit breaker immediately fails all requests without calling the downstream 
            service. This prevents resource waste and allows the failing service time to recover.
          </p>
          <pre style={styles.codeBlock}>
{`Open State Behavior:

Failure Protection:
┌─────────────┐    Request     ┌─────────────┐                ┌─────────────┐
│   Client    │ ─────────────→ │   Circuit   │       ✗        │   Service   │
│             │                │   Breaker   │                │  (Failing)  │
│             │ ←───────────── │   (OPEN)    │                │             │
└─────────────┘  Fast Fail     └─────────────┘                └─────────────┘

Characteristics:
✗ Block all requests
✓ Return immediately (fast fail)
✓ Execute fallback logic
✓ Start recovery timer

Benefits:
- Prevents resource exhaustion
- Stops cascading failures
- Reduces load on failing service
- Provides immediate feedback
- Allows service recovery time

Timeout Period:
- Fixed timeout (e.g., 60 seconds)
- Exponential backoff
- Configurable based on service type
- Can be adjusted dynamically

Transition to Half-Open:
AFTER timeout_period ELAPSED
THEN state = HALF_OPEN`}
          </pre>

          <h3 style={styles.heading}>Half-Open State</h3>
          <p style={styles.paragraph}>
            In the half-open state, the circuit breaker allows a limited number of test requests to determine 
            if the downstream service has recovered. Based on these test results, it either closes or reopens.
          </p>
          <pre style={styles.codeBlock}>
{`Half-Open State Behavior:

Recovery Testing:
┌─────────────┐    Request     ┌─────────────┐   Test Req     ┌─────────────┐
│   Client    │ ─────────────→ │   Circuit   │ ─────────────→ │   Service   │
│             │                │   Breaker   │                │ (Recovery?) │
│             │ ←───────────── │ (HALF-OPEN) │ ←───────────── │             │
└─────────────┘    Response    └─────────────┘    Response    └─────────────┘

Characteristics:
✓ Allow limited test requests
✓ Monitor test results carefully
✓ Block excess requests
✓ Quick state transitions

Test Request Strategies:
1. Single Request Test:
   - Allow only 1 request
   - Success → CLOSED, Failure → OPEN

2. Limited Request Test:
   - Allow N requests (e.g., 5)
   - Success rate > threshold → CLOSED
   - Failure rate > threshold → OPEN

3. Time-based Test:
   - Allow requests for T seconds
   - Evaluate overall performance

Transition Logic:
IF test_success_rate >= recovery_threshold
THEN state = CLOSED
ELSE state = OPEN (with increased timeout)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Examples</h2>
          
          <h3 style={styles.heading}>Basic Circuit Breaker</h3>
          <p style={styles.paragraph}>
            Here's a comprehensive implementation of a circuit breaker with all three states:
          </p>
          <pre style={styles.codeBlock}>
{`Basic Circuit Breaker Implementation:

import time
import threading
from enum import Enum
from typing import Callable, Any, Optional
from dataclasses import dataclass
from collections import deque

class CircuitState(Enum):
    CLOSED = "closed"
    OPEN = "open"
    HALF_OPEN = "half_open"

@dataclass
class CircuitBreakerConfig:
    failure_threshold: int = 5          # Failures to trigger open
    recovery_timeout: float = 60.0      # Seconds before half-open
    success_threshold: int = 3          # Successes to close from half-open
    timeout: float = 10.0               # Request timeout
    expected_exception: tuple = (Exception,)  # Exceptions to count as failures

class CircuitBreakerError(Exception):
    """Exception raised when circuit breaker is open"""
    pass

class CircuitBreaker:
    def __init__(self, config: CircuitBreakerConfig):
        self.config = config
        self.state = CircuitState.CLOSED
        self.failure_count = 0
        self.success_count = 0
        self.last_failure_time = None
        self.lock = threading.Lock()
        
        # For monitoring and metrics
        self.total_requests = 0
        self.total_failures = 0
        self.total_successes = 0
        self.state_changes = []
    
    def call(self, func: Callable, *args, **kwargs) -> Any:
        """Execute function with circuit breaker protection"""
        with self.lock:
            self.total_requests += 1
            
            # Check current state and decide whether to proceed
            if self.state == CircuitState.OPEN:
                if self._should_attempt_reset():
                    self._set_state(CircuitState.HALF_OPEN)
                else:
                    self.total_failures += 1
                    raise CircuitBreakerError("Circuit breaker is OPEN")
            
            elif self.state == CircuitState.HALF_OPEN:
                # In half-open, we're testing - be more cautious
                pass
        
        # Execute the function
        try:
            result = self._execute_with_timeout(func, *args, **kwargs)
            self._on_success()
            return result
            
        except self.config.expected_exception as e:
            self._on_failure()
            raise e
    
    def _execute_with_timeout(self, func: Callable, *args, **kwargs) -> Any:
        """Execute function with timeout"""
        import signal
        
        def timeout_handler(signum, frame):
            raise TimeoutError("Function execution timed out")
        
        # Set up timeout (Unix-like systems only)
        try:
            old_handler = signal.signal(signal.SIGALRM, timeout_handler)
            signal.alarm(int(self.config.timeout))
            
            result = func(*args, **kwargs)
            
            signal.alarm(0)  # Cancel alarm
            signal.signal(signal.SIGALRM, old_handler)
            
            return result
            
        except AttributeError:
            # Fallback for systems without signal support (Windows)
            return func(*args, **kwargs)
    
    def _on_success(self):
        """Handle successful request"""
        with self.lock:
            self.total_successes += 1
            
            if self.state == CircuitState.HALF_OPEN:
                self.success_count += 1
                if self.success_count >= self.config.success_threshold:
                    self._reset()
            elif self.state == CircuitState.CLOSED:
                # Reset failure count on success
                self.failure_count = 0
    
    def _on_failure(self):
        """Handle failed request"""
        with self.lock:
            self.total_failures += 1
            self.failure_count += 1
            self.last_failure_time = time.time()
            
            if self.state == CircuitState.CLOSED:
                if self.failure_count >= self.config.failure_threshold:
                    self._set_state(CircuitState.OPEN)
            elif self.state == CircuitState.HALF_OPEN:
                self._set_state(CircuitState.OPEN)
    
    def _should_attempt_reset(self) -> bool:
        """Check if enough time has passed to attempt reset"""
        return (time.time() - self.last_failure_time) >= self.config.recovery_timeout
    
    def _reset(self):
        """Reset circuit breaker to closed state"""
        self.failure_count = 0
        self.success_count = 0
        self._set_state(CircuitState.CLOSED)
    
    def _set_state(self, new_state: CircuitState):
        """Change circuit breaker state"""
        old_state = self.state
        self.state = new_state
        self.state_changes.append({
            'timestamp': time.time(),
            'from_state': old_state.value,
            'to_state': new_state.value,
            'failure_count': self.failure_count,
            'total_requests': self.total_requests
        })
        
        print(f"Circuit Breaker: {old_state.value} → {new_state.value}")
    
    def get_state(self) -> CircuitState:
        """Get current state"""
        return self.state
    
    def get_metrics(self) -> dict:
        """Get circuit breaker metrics"""
        return {
            'state': self.state.value,
            'failure_count': self.failure_count,
            'success_count': self.success_count,
            'total_requests': self.total_requests,
            'total_failures': self.total_failures,
            'total_successes': self.total_successes,
            'failure_rate': self.total_failures / max(1, self.total_requests),
            'state_changes': self.state_changes
        }

# Usage Example
def unreliable_service():
    """Simulate an unreliable service"""
    import random
    if random.random() < 0.7:  # 70% failure rate
        raise ConnectionError("Service unavailable")
    return "Success!"

# Configure and use circuit breaker
config = CircuitBreakerConfig(
    failure_threshold=3,
    recovery_timeout=5.0,
    success_threshold=2,
    timeout=2.0
)

circuit_breaker = CircuitBreaker(config)

# Test the circuit breaker
for i in range(20):
    try:
        result = circuit_breaker.call(unreliable_service)
        print(f"Request {i+1}: {result}")
    except (CircuitBreakerError, ConnectionError) as e:
        print(f"Request {i+1}: Failed - {e}")
    
    time.sleep(1)
    
    # Print metrics every 5 requests
    if (i + 1) % 5 == 0:
        metrics = circuit_breaker.get_metrics()
        print(f"Metrics: State={metrics['state']}, "
              f"Failure Rate={metrics['failure_rate']:.2f}")
        print("---")`}
          </pre>

          <h3 style={styles.heading}>Advanced Circuit Breaker with Fallbacks</h3>
          <p style={styles.paragraph}>
            Enhance the circuit breaker with fallback mechanisms and monitoring capabilities:
          </p>
          <pre style={styles.codeBlock}>
{`Advanced Circuit Breaker with Fallbacks:

from typing import Union, Dict, List
import logging
from contextlib import contextmanager

class FallbackCircuitBreaker(CircuitBreaker):
    def __init__(self, config: CircuitBreakerConfig, fallback_func: Optional[Callable] = None):
        super().__init__(config)
        self.fallback_func = fallback_func
        self.recent_requests = deque(maxlen=100)  # Track recent request history
        self.logger = logging.getLogger(__name__)
    
    def call_with_fallback(self, func: Callable, *args, **kwargs) -> Any:
        """Execute function with fallback support"""
        try:
            return self.call(func, *args, **kwargs)
        except CircuitBreakerError:
            if self.fallback_func:
                self.logger.info("Circuit breaker open, executing fallback")
                return self.fallback_func(*args, **kwargs)
            else:
                raise
    
    def _on_success(self):
        """Enhanced success tracking"""
        super()._on_success()
        self.recent_requests.append({
            'timestamp': time.time(),
            'success': True,
            'response_time': None  # Could be measured
        })
    
    def _on_failure(self):
        """Enhanced failure tracking"""
        super()._on_failure()
        self.recent_requests.append({
            'timestamp': time.time(),
            'success': False,
            'response_time': None
        })
    
    def get_health_metrics(self) -> Dict:
        """Get detailed health metrics"""
        recent_window = time.time() - 60  # Last minute
        recent_requests = [
            req for req in self.recent_requests 
            if req['timestamp'] > recent_window
        ]
        
        if not recent_requests:
            return {'recent_success_rate': 0, 'recent_request_count': 0}
        
        recent_successes = sum(1 for req in recent_requests if req['success'])
        
        return {
            'recent_success_rate': recent_successes / len(recent_requests),
            'recent_request_count': len(recent_requests),
            'recent_failure_count': len(recent_requests) - recent_successes
        }

# Service-Specific Circuit Breakers
class ServiceCircuitBreakerManager:
    def __init__(self):
        self.circuit_breakers: Dict[str, FallbackCircuitBreaker] = {}
        self.default_config = CircuitBreakerConfig()
    
    def get_circuit_breaker(self, service_name: str, 
                          config: Optional[CircuitBreakerConfig] = None,
                          fallback_func: Optional[Callable] = None) -> FallbackCircuitBreaker:
        """Get or create circuit breaker for service"""
        if service_name not in self.circuit_breakers:
            service_config = config or self.default_config
            self.circuit_breakers[service_name] = FallbackCircuitBreaker(
                service_config, fallback_func
            )
        return self.circuit_breakers[service_name]
    
    def call_service(self, service_name: str, func: Callable, *args, **kwargs) -> Any:
        """Call service through its circuit breaker"""
        cb = self.get_circuit_breaker(service_name)
        return cb.call_with_fallback(func, *args, **kwargs)
    
    def get_all_metrics(self) -> Dict[str, Dict]:
        """Get metrics for all services"""
        return {
            service: cb.get_metrics() 
            for service, cb in self.circuit_breakers.items()
        }

# Decorator for Easy Integration
def circuit_breaker(service_name: str, 
                   config: Optional[CircuitBreakerConfig] = None,
                   fallback: Optional[Callable] = None):
    """Decorator to add circuit breaker to functions"""
    def decorator(func):
        def wrapper(*args, **kwargs):
            manager = ServiceCircuitBreakerManager()
            return manager.call_service(service_name, func, *args, **kwargs)
        return wrapper
    return decorator

# Usage Examples
manager = ServiceCircuitBreakerManager()

# Database service with fallback
def get_user_from_cache(user_id):
    return {'id': user_id, 'name': 'Cached User', 'source': 'cache'}

def get_user_from_db(user_id):
    # Simulate database call
    import random
    if random.random() < 0.3:  # 30% failure rate
        raise ConnectionError("Database connection failed")
    return {'id': user_id, 'name': 'DB User', 'source': 'database'}

# Configure circuit breaker with fallback
db_config = CircuitBreakerConfig(
    failure_threshold=2,
    recovery_timeout=10.0,
    success_threshold=1
)

db_circuit_breaker = manager.get_circuit_breaker(
    'user_database',
    config=db_config,
    fallback_func=get_user_from_cache
)

# Use the service
for i in range(10):
    try:
        user = db_circuit_breaker.call_with_fallback(get_user_from_db, user_id=i)
        print(f"User {i}: {user}")
    except Exception as e:
        print(f"User {i}: Error - {e}")

# Using decorator approach
@circuit_breaker('payment_service', 
                config=CircuitBreakerConfig(failure_threshold=1),
                fallback=lambda amount: {'status': 'queued', 'amount': amount})
def process_payment(amount):
    import random
    if random.random() < 0.5:
        raise Exception("Payment service unavailable")
    return {'status': 'completed', 'amount': amount}

# Test payment service
for amount in [100, 200, 300, 400, 500]:
    try:
        result = process_payment(amount)
        print(f"Payment $\{amount\}: \{result\}")
    except Exception as e:
        print(f"Payment $\{amount\}: Error - \{e\}")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Circuit Breaker Patterns</h2>
          
          <h3 style={styles.heading}>Bulkhead Pattern Integration</h3>
          <p style={styles.paragraph}>
            Combine circuit breakers with the bulkhead pattern to isolate different types of operations:
          </p>
          <pre style={styles.codeBlock}>
{`Bulkhead + Circuit Breaker Pattern:

class BulkheadCircuitBreaker:
    def __init__(self):
        # Separate circuit breakers for different operation types
        self.circuit_breakers = {
            'read_operations': FallbackCircuitBreaker(
                CircuitBreakerConfig(failure_threshold=5, recovery_timeout=30)
            ),
            'write_operations': FallbackCircuitBreaker(
                CircuitBreakerConfig(failure_threshold=2, recovery_timeout=60)
            ),
            'admin_operations': FallbackCircuitBreaker(
                CircuitBreakerConfig(failure_threshold=1, recovery_timeout=120)
            )
        }
    
    def execute_read(self, func, *args, **kwargs):
        """Execute read operation with dedicated circuit breaker"""
        return self.circuit_breakers['read_operations'].call(func, *args, **kwargs)
    
    def execute_write(self, func, *args, **kwargs):
        """Execute write operation with dedicated circuit breaker"""
        return self.circuit_breakers['write_operations'].call(func, *args, **kwargs)
    
    def execute_admin(self, func, *args, **kwargs):
        """Execute admin operation with dedicated circuit breaker"""
        return self.circuit_breakers['admin_operations'].call(func, *args, **kwargs)

# Usage
bulkhead_cb = BulkheadCircuitBreaker()

# Read operations continue even if writes are failing
try:
    data = bulkhead_cb.execute_read(fetch_user_data, user_id=123)
except CircuitBreakerError:
    print("Read operations are currently unavailable")

try:
    result = bulkhead_cb.execute_write(update_user_profile, user_id=123, data={})
except CircuitBreakerError:
    print("Write operations are currently unavailable")`}
          </pre>

          <h3 style={styles.heading}>Adaptive Circuit Breaker</h3>
          <p style={styles.paragraph}>
            Implement an adaptive circuit breaker that adjusts thresholds based on historical performance:
          </p>
          <pre style={styles.codeBlock}>
{`Adaptive Circuit Breaker:

import statistics
from collections import defaultdict

class AdaptiveCircuitBreaker(CircuitBreaker):
    def __init__(self, config: CircuitBreakerConfig):
        super().__init__(config)
        self.performance_history = deque(maxlen=1000)
        self.hourly_stats = defaultdict(list)
        self.adaptive_threshold = config.failure_threshold
        self.last_adaptation = time.time()
        self.adaptation_interval = 3600  # 1 hour
    
    def _on_success(self):
        """Track performance for adaptation"""
        super()._on_success()
        self.performance_history.append({
            'timestamp': time.time(),
            'success': True,
            'hour': int(time.time() // 3600)
        })
        self._maybe_adapt_threshold()
    
    def _on_failure(self):
        """Track failures for adaptation"""
        super()._on_failure()
        self.performance_history.append({
            'timestamp': time.time(),
            'success': False,
            'hour': int(time.time() // 3600)
        })
        self._maybe_adapt_threshold()
    
    def _maybe_adapt_threshold(self):
        """Adapt failure threshold based on historical performance"""
        now = time.time()
        if now - self.last_adaptation < self.adaptation_interval:
            return
        
        # Calculate historical failure rates by hour
        current_hour = int(now // 3600)
        recent_hours = [current_hour - i for i in range(24)]  # Last 24 hours
        
        hourly_failure_rates = []
        for hour in recent_hours:
            hour_requests = [r for r in self.performance_history if r['hour'] == hour]
            if hour_requests:
                failures = sum(1 for r in hour_requests if not r['success'])
                failure_rate = failures / len(hour_requests)
                hourly_failure_rates.append(failure_rate)
        
        if hourly_failure_rates:
            avg_failure_rate = statistics.mean(hourly_failure_rates)
            std_failure_rate = statistics.stdev(hourly_failure_rates) if len(hourly_failure_rates) > 1 else 0
            
            # Adapt threshold based on historical patterns
            if avg_failure_rate < 0.1:  # Very reliable service
                self.adaptive_threshold = max(self.config.failure_threshold + 2, 8)
            elif avg_failure_rate > 0.3:  # Unreliable service
                self.adaptive_threshold = max(self.config.failure_threshold - 1, 2)
            else:
                self.adaptive_threshold = self.config.failure_threshold
            
            print(f"Adapted failure threshold to {self.adaptive_threshold} "
                  f"(avg failure rate: {avg_failure_rate:.3f})")
        
        self.last_adaptation = now
    
    def _on_failure(self):
        """Use adaptive threshold"""
        with self.lock:
            self.total_failures += 1
            self.failure_count += 1
            self.last_failure_time = time.time()
            
            if self.state == CircuitState.CLOSED:
                if self.failure_count >= self.adaptive_threshold:  # Use adaptive threshold
                    self._set_state(CircuitState.OPEN)
            elif self.state == CircuitState.HALF_OPEN:
                self._set_state(CircuitState.OPEN)

# Distributed Circuit Breaker with Redis
class DistributedCircuitBreaker:
    def __init__(self, redis_client, service_name: str, config: CircuitBreakerConfig):
        self.redis = redis_client
        self.service_name = service_name
        self.config = config
        self.local_cache = {'state': CircuitState.CLOSED, 'last_update': 0}
        self.cache_ttl = 5  # Cache state for 5 seconds
    
    def _get_redis_keys(self):
        """Get Redis keys for this service"""
        return {
            'state': f"cb:{self.service_name}:state",
            'failure_count': f"cb:{self.service_name}:failures",
            'last_failure': f"cb:{self.service_name}:last_failure",
            'success_count': f"cb:{self.service_name}:successes"
        }
    
    def _get_state(self) -> CircuitState:
        """Get current state from Redis with local caching"""
        now = time.time()
        if now - self.local_cache['last_update'] < self.cache_ttl:
            return self.local_cache['state']
        
        keys = self._get_redis_keys()
        state_str = self.redis.get(keys['state'])
        
        if state_str:
            state = CircuitState(state_str.decode())
        else:
            state = CircuitState.CLOSED
        
        self.local_cache = {'state': state, 'last_update': now}
        return state
    
    def call(self, func: Callable, *args, **kwargs) -> Any:
        """Execute function with distributed circuit breaker"""
        state = self._get_state()
        
        if state == CircuitState.OPEN:
            # Check if we should transition to half-open
            keys = self._get_redis_keys()
            last_failure = self.redis.get(keys['last_failure'])
            
            if last_failure:
                if time.time() - float(last_failure) >= self.config.recovery_timeout:
                    self._set_state(CircuitState.HALF_OPEN)
                else:
                    raise CircuitBreakerError("Circuit breaker is OPEN")
            else:
                self._set_state(CircuitState.CLOSED)
        
        # Execute function
        try:
            result = func(*args, **kwargs)
            self._on_success()
            return result
        except Exception as e:
            self._on_failure()
            raise e
    
    def _on_success(self):
        """Record success in Redis"""
        keys = self._get_redis_keys()
        pipe = self.redis.pipeline()
        
        if self._get_state() == CircuitState.HALF_OPEN:
            pipe.incr(keys['success_count'])
            pipe.expire(keys['success_count'], 300)  # 5 minute expiry
            
            # Check if we should close the circuit
            pipe.execute()
            success_count = int(self.redis.get(keys['success_count']) or 0)
            
            if success_count >= self.config.success_threshold:
                self._set_state(CircuitState.CLOSED)
                self.redis.delete(keys['failure_count'], keys['success_count'])
        else:
            # Reset failure count on success
            pipe.delete(keys['failure_count'])
            pipe.execute()
    
    def _on_failure(self):
        """Record failure in Redis"""
        keys = self._get_redis_keys()
        pipe = self.redis.pipeline()
        
        pipe.incr(keys['failure_count'])
        pipe.set(keys['last_failure'], time.time())
        pipe.expire(keys['failure_count'], 300)
        pipe.expire(keys['last_failure'], 300)
        
        results = pipe.execute()
        failure_count = results[0]
        
        if failure_count >= self.config.failure_threshold:
            self._set_state(CircuitState.OPEN)
    
    def _set_state(self, new_state: CircuitState):
        """Set state in Redis"""
        keys = self._get_redis_keys()
        self.redis.setex(keys['state'], 300, new_state.value)
        self.local_cache = {'state': new_state, 'last_update': time.time()}

# Usage with Redis
import redis

redis_client = redis.Redis(host='localhost', port=6379, db=0)
distributed_cb = DistributedCircuitBreaker(
    redis_client, 
    'user_service',
    CircuitBreakerConfig(failure_threshold=3)
)

# This circuit breaker state is shared across all application instances
try:
    result = distributed_cb.call(call_user_service, user_id=123)
except CircuitBreakerError:
    print("User service is currently unavailable")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Monitoring and Observability</h2>
          
          <h3 style={styles.heading}>Circuit Breaker Metrics</h3>
          <p style={styles.paragraph}>
            Implement comprehensive monitoring for circuit breaker health and performance:
          </p>
          <pre style={styles.codeBlock}>
{`Circuit Breaker Monitoring:

import json
from datetime import datetime, timedelta

class CircuitBreakerMonitor:
    def __init__(self, circuit_breaker: CircuitBreaker):
        self.circuit_breaker = circuit_breaker
        self.metrics_history = []
        self.alerts = []
    
    def collect_metrics(self) -> dict:
        """Collect current metrics"""
        metrics = self.circuit_breaker.get_metrics()
        
        # Add timestamp and additional calculated metrics
        metrics.update({
            'timestamp': time.time(),
            'uptime_percentage': self._calculate_uptime(),
            'mean_time_to_recovery': self._calculate_mttr(),
            'failure_rate_trend': self._calculate_failure_trend()
        })
        
        self.metrics_history.append(metrics)
        
        # Keep only last 24 hours of metrics
        cutoff = time.time() - 86400
        self.metrics_history = [
            m for m in self.metrics_history if m['timestamp'] > cutoff
        ]
        
        return metrics
    
    def _calculate_uptime(self) -> float:
        """Calculate service uptime percentage"""
        if not self.metrics_history:
            return 100.0
        
        total_time = 0
        down_time = 0
        
        for i in range(1, len(self.metrics_history)):
            prev_metrics = self.metrics_history[i-1]
            curr_metrics = self.metrics_history[i]
            
            interval = curr_metrics['timestamp'] - prev_metrics['timestamp']
            total_time += interval
            
            if prev_metrics['state'] == 'open':
                down_time += interval
        
        return ((total_time - down_time) / total_time * 100) if total_time > 0 else 100.0
    
    def _calculate_mttr(self) -> float:
        """Calculate Mean Time To Recovery"""
        recovery_times = []
        
        for change in self.circuit_breaker.state_changes:
            if change['to_state'] == 'closed' and change['from_state'] in ['open', 'half_open']:
                # Find when it first opened
                for prev_change in reversed(self.circuit_breaker.state_changes):
                    if prev_change['to_state'] == 'open' and prev_change['timestamp'] < change['timestamp']:
                        recovery_time = change['timestamp'] - prev_change['timestamp']
                        recovery_times.append(recovery_time)
                        break
        
        return sum(recovery_times) / len(recovery_times) if recovery_times else 0
    
    def _calculate_failure_trend(self) -> str:
        """Calculate failure rate trend"""
        if len(self.metrics_history) < 2:
            return 'stable'
        
        recent_metrics = self.metrics_history[-5:]  # Last 5 data points
        if len(recent_metrics) < 2:
            return 'stable'
        
        failure_rates = [m['failure_rate'] for m in recent_metrics]
        
        # Simple trend calculation
        if failure_rates[-1] > failure_rates[0] * 1.2:
            return 'increasing'
        elif failure_rates[-1] < failure_rates[0] * 0.8:
            return 'decreasing'
        else:
            return 'stable'
    
    def check_alerts(self) -> List[dict]:
        """Check for alert conditions"""
        current_metrics = self.collect_metrics()
        alerts = []
        
        # High failure rate alert
        if current_metrics['failure_rate'] > 0.5:
            alerts.append({
                'type': 'high_failure_rate',
                'severity': 'critical',
                'message': f"Failure rate is {current_metrics['failure_rate']:.2%}",
                'timestamp': time.time()
            })
        
        # Circuit breaker stuck open
        if current_metrics['state'] == 'open':
            open_duration = time.time() - (self.circuit_breaker.last_failure_time or 0)
            if open_duration > 300:  # 5 minutes
                alerts.append({
                    'type': 'circuit_stuck_open',
                    'severity': 'warning',
                    'message': f"Circuit breaker has been open for {open_duration:.0f} seconds",
                    'timestamp': time.time()
                })
        
        # Frequent state changes (flapping)
        recent_changes = [
            c for c in self.circuit_breaker.state_changes 
            if c['timestamp'] > time.time() - 300  # Last 5 minutes
        ]
        
        if len(recent_changes) > 10:
            alerts.append({
                'type': 'circuit_flapping',
                'severity': 'warning',
                'message': f"Circuit breaker changed state {len(recent_changes)} times in 5 minutes",
                'timestamp': time.time()
            })
        
        self.alerts.extend(alerts)
        return alerts
    
    def generate_report(self) -> dict:
        """Generate comprehensive health report"""
        current_metrics = self.collect_metrics()
        
        return {
            'service_health': {
                'current_state': current_metrics['state'],
                'failure_rate': current_metrics['failure_rate'],
                'uptime_percentage': current_metrics['uptime_percentage'],
                'total_requests': current_metrics['total_requests'],
                'mean_time_to_recovery': current_metrics['mean_time_to_recovery']
            },
            'trends': {
                'failure_rate_trend': current_metrics['failure_rate_trend'],
                'recent_state_changes': len([
                    c for c in self.circuit_breaker.state_changes 
                    if c['timestamp'] > time.time() - 3600
                ])
            },
            'alerts': self.check_alerts(),
            'recommendations': self._generate_recommendations(current_metrics)
        }
    
    def _generate_recommendations(self, metrics: dict) -> List[str]:
        """Generate operational recommendations"""
        recommendations = []
        
        if metrics['failure_rate'] > 0.3:
            recommendations.append("Consider investigating downstream service health")
        
        if metrics['uptime_percentage'] < 95:
            recommendations.append("Review circuit breaker thresholds - may be too sensitive")
        
        if metrics['mean_time_to_recovery'] > 300:
            recommendations.append("Recovery time is high - check service deployment and scaling")
        
        if metrics['failure_rate_trend'] == 'increasing':
            recommendations.append("Failure rate is trending up - monitor closely")
        
        return recommendations

# Prometheus Metrics Integration
class PrometheusCircuitBreakerMetrics:
    def __init__(self, circuit_breaker: CircuitBreaker, service_name: str):
        self.circuit_breaker = circuit_breaker
        self.service_name = service_name
        
        # Initialize Prometheus metrics (pseudo-code)
        self.request_counter = Counter(
            'circuit_breaker_requests_total',
            'Total requests through circuit breaker',
            ['service', 'state', 'result']
        )
        
        self.state_gauge = Gauge(
            'circuit_breaker_state',
            'Current circuit breaker state (0=closed, 1=half-open, 2=open)',
            ['service']
        )
        
        self.failure_rate_gauge = Gauge(
            'circuit_breaker_failure_rate',
            'Current failure rate',
            ['service']
        )
    
    def record_request(self, success: bool):
        """Record request metrics"""
        state = self.circuit_breaker.get_state().value
        result = 'success' if success else 'failure'
        
        self.request_counter.labels(
            service=self.service_name,
            state=state,
            result=result
        ).inc()
        
        # Update gauges
        state_value = {'closed': 0, 'half_open': 1, 'open': 2}[state]
        self.state_gauge.labels(service=self.service_name).set(state_value)
        
        metrics = self.circuit_breaker.get_metrics()
        self.failure_rate_gauge.labels(service=self.service_name).set(
            metrics['failure_rate']
        )

# Usage
monitor = CircuitBreakerMonitor(circuit_breaker)

# Collect metrics periodically
import threading

def metrics_collector():
    while True:
        report = monitor.generate_report()
        print(json.dumps(report, indent=2))
        
        # Send alerts if any
        if report['alerts']:
            for alert in report['alerts']:
                print(f"ALERT: {alert['message']}")
        
        time.sleep(60)  # Collect every minute

# Start monitoring thread
metrics_thread = threading.Thread(target=metrics_collector, daemon=True)
metrics_thread.start()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Netflix Hystrix</h3>
          <p style={styles.paragraph}>
            Netflix developed Hystrix, a comprehensive circuit breaker library for Java applications. It provides 
            circuit breaking, bulkhead isolation, fallbacks, and real-time monitoring. Hystrix helped Netflix 
            achieve high availability across their microservices architecture.
          </p>

          <h3 style={styles.heading}>AWS Application Load Balancer</h3>
          <p style={styles.paragraph}>
            AWS ALB implements circuit breaker patterns in their health checks and target group management. 
            Unhealthy targets are automatically removed from rotation and periodically tested for recovery, 
            similar to circuit breaker half-open state.
          </p>

          <h3 style={styles.heading}>Kubernetes Readiness Probes</h3>
          <p style={styles.paragraph}>
            Kubernetes uses readiness probes as a form of circuit breaker, removing pods from service discovery 
            when they fail health checks and adding them back when they recover, preventing traffic from reaching 
            unhealthy instances.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Set appropriate thresholds:</strong> Configure failure thresholds based on service SLAs 
              and historical performance data rather than arbitrary numbers.
            </li>
            <li style={styles.listItem}>
              <strong>Implement meaningful fallbacks:</strong> Provide cached data, default responses, or 
              alternative service calls rather than just failing fast.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor and alert:</strong> Track circuit breaker state changes, failure rates, and 
              recovery times to identify patterns and optimize configurations.
            </li>
            <li style={styles.listItem}>
              <strong>Test failure scenarios:</strong> Regularly test circuit breaker behavior through chaos 
              engineering and failure injection to ensure it works as expected.
            </li>
            <li style={styles.listItem}>
              <strong>Use distributed circuit breakers:</strong> For multi-instance deployments, share circuit 
              breaker state across instances to prevent cascading failures.
            </li>
            <li style={styles.listItem}>
              <strong>Combine with other patterns:</strong> Use circuit breakers alongside retry mechanisms, 
              bulkheads, and timeouts for comprehensive resilience.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Circuit breakers are essential for building resilient distributed systems. They prevent cascading 
            failures, provide fast failure detection, and enable automatic recovery testing while maintaining 
            system stability during partial outages.
          </p>
          <p style={styles.paragraph}>
            Implement circuit breakers early in your architecture, monitor their behavior closely, and combine 
            them with other resilience patterns for comprehensive fault tolerance. Remember that the goal is not 
            just to fail fast, but to fail gracefully and recover automatically.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CircuitBreakers;
